import { Observable } from "rxjs";
import { Epic } from "redux-observable";
import { List, fromJS } from "immutable";
import diff from "immutablediff";
import { formatMessage } from "lib/globalize-util";
import siteConfig from "site-config";
import { ajax } from "lib/observable";
import { IStore } from "reducers";
import cloneDeep from "lodash/cloneDeep";
import { getCollectionDeliveryPurchaseOrders, getAllocationDimensionsBySubtype } from "reducers/selectors/collection";
import { COLLECTIONS, MUTATE_OPERATIONS_SENT, MUTATE_OPERATION_ENHANCED } from "constants/action-types";
import { DELIVERY_PURCHASE_ORDERS_ID, ALLOCATION_DIMENSIONS_NAMESPACE } from "constants/product-collection";
import {
    MutateDeliveryPurchaseOrderAction,
    mutateDeliveryPurchaseOrder,
    startReconcilePOWithAllocationDimensions,
} from "actions/collections";
import { listInsertOperation, listSetOperation, OperationType } from "actions/mutate";
import { add as snackbarAdd } from "actions/snackbars";
import {
    AllocationDimensionKind,
    AllocationDimensionFields,
    AllocationDimensionValueFields,
} from "helpers/allocation-dimension";
import { PURCHASE_ORDER_DOOR_GROUP_FIELD } from "helpers/door-collection";
import {
    normalizeToRequestDeliveryPurchaseOrder,
    IDeliveryPurchaseOrder,
    reconcilePOWithAllocationDimensionToMutate,
} from "helpers/delivery-purchase-order";
import { amphibiousToJS } from "helpers/objUtils";
import { getAndIncrementRequestId, mapToOperation } from "./helpers";

// TODO: Add UT
// Helper function to make sure an operation is not creating / setting duplicate delivery PO.
// - Insert existing composite keys of delivery_id and purchase_order_door_group_id
// - Set existing composite keys of delivery_id and purchase_order_door_group_id into different index
const checkNonDuplicateOperation = (
    index: number,
    deliveryPurchaseOrder: IDeliveryPurchaseOrder,
    deliveryPurchaseOrders: IDeliveryPurchaseOrder[]
): boolean => {
    let editingSelf = false;
    let selfIndex = -1;

    deliveryPurchaseOrders.forEach((existingDeliveryPurchaseOrder, existingIndex) => {
        if (
            deliveryPurchaseOrder.delivery_id === existingDeliveryPurchaseOrder.delivery_id &&
            deliveryPurchaseOrder.purchase_order_door_group_id ===
                existingDeliveryPurchaseOrder.purchase_order_door_group_id
        ) {
            // Use first occurance
            if (selfIndex === -1) {
                selfIndex = existingIndex;
            }

            // Only set `editingSelf` to true if it's same id AND same composite keys (delivery_id, purchase_order_door_group_id)
            if (existingDeliveryPurchaseOrder.id === deliveryPurchaseOrder.id) {
                editingSelf = true;
            }
        }
    });

    const isValidSet = editingSelf || selfIndex === index;
    const isValidInsert = !deliveryPurchaseOrder.id && selfIndex === -1;

    return isValidInsert || isValidSet;
};

export const mutateCollectionDeliveryPurchaseOrderEpic: Epic<MutateDeliveryPurchaseOrderAction, IStore> = (
    action$,
    store$
) =>
    action$
        .ofType(COLLECTIONS.COLLECTION.DELIVERY_PURCHASE_ORDER.MUTATE.START)
        .filter(({ payload: { collectionId, deliveryPurchaseOrder, index } }) => {
            const state = store$.getState();
            const deliveryPurchaseOrders = getCollectionDeliveryPurchaseOrders(state, collectionId);
            return checkNonDuplicateOperation(index, deliveryPurchaseOrder, deliveryPurchaseOrders);
        })
        .concatMap((action) => {
            const { collectionId, deliveryPurchaseOrder, index } = action.payload;
            const state = store$.getState();
            const deliveryPurchaseOrders = getCollectionDeliveryPurchaseOrders(state, collectionId);
            const mutatingItemByIndex = deliveryPurchaseOrders[index];
            const immutableItem = fromJS(deliveryPurchaseOrder);

            const actions = [];

            if (!mutatingItemByIndex) {
                actions.push(listInsertOperation(DELIVERY_PURCHASE_ORDERS_ID, immutableItem, index, action.meta));
            } else {
                const deltas = diff(fromJS(mutatingItemByIndex), immutableItem);
                actions.push(listSetOperation(DELIVERY_PURCHASE_ORDERS_ID, index, immutableItem, deltas, action.meta));
            }

            return actions;
        });

export const syncCollectionDeliveryPurchaseOrderEpic = (action$, store$) =>
    action$
        .ofType(COLLECTIONS.COLLECTION.DELIVERY_PURCHASE_ORDER.SYNC.START)
        // Dont proceed if there is something sending or there is nothing to send
        .filter(({ payload: { objectId } }) => {
            const { mutate } = store$.getState();
            const { sentMutations, uncommittedMutations } = mutate;
            const sentMutation = sentMutations.get(objectId);
            const uncommittedMutation = uncommittedMutations.get(objectId) || List();

            return !sentMutation && !!uncommittedMutation.size;
        })
        .map(({ payload: { objectId } }: { payload: { objectId: string } }) => {
            const { collection, mutate } = store$.getState();
            const { committedHEADs, uncommittedMutations } = mutate;
            const { sessionId, merchantId } = collection;

            const committedHEAD = committedHEADs.get(objectId);

            const operations = [];
            uncommittedMutations.get(objectId).forEach((mutation) => {
                mutation.operations.forEach((operation) => {
                    const normalizedOperation = cloneDeep(operation);
                    // BE requires purchase order details nested inside po
                    if (normalizedOperation.value) {
                        normalizedOperation.value = normalizeToRequestDeliveryPurchaseOrder(
                            amphibiousToJS(normalizedOperation.value)
                        );
                    }
                    operations.push(mapToOperation(normalizedOperation));
                });
            });

            const requestId = getAndIncrementRequestId(objectId);
            const revision = parseInt(committedHEAD.revision, 10) + 1;
            const dataToSave = {
                operations,
                revision,
                req_id: requestId,
                merchant_id: merchantId,
            };

            store$.dispatch({
                type: MUTATE_OPERATIONS_SENT,
                payload: {
                    requestId,
                    objectId,
                    revision,
                    objectType: committedHEAD.object_type,
                },
            });

            return { dataToSave, sessionId, collectionId: committedHEAD.document_id };
        })
        // Make it a queue, wait for response before continuing
        .concatMap(({ dataToSave, collectionId, sessionId }) => {
            const url = `/v1/collections/${collectionId}/ref/delivery_purchase_orders/mutate?sid=${sessionId}`;
            return ajax({
                body: dataToSave,
                method: "POST",
                url: siteConfig.goAPI + url,
            })
                .map(() => ({
                    payload: { collectionId },
                    type: COLLECTIONS.COLLECTION.DELIVERY_PURCHASE_ORDER.SYNC.COMPLETE,
                }))
                .catch(() => {
                    return Observable.merge(
                        Observable.of(
                            snackbarAdd({
                                message: formatMessage("There was an error while saving. Please try again."),
                            })
                        )
                    );
                });
        });

export const allocationDimensionMutateHandlerEpic = (action$, store$) =>
    action$
        .ofType(MUTATE_OPERATION_ENHANCED)
        .filter(
            ({
                payload: {
                    HEAD: { document_id, object_type },
                },
            }) => {
                const state = store$.getState();
                const collectionId = document_id;

                const genericAllocationDimensions = getAllocationDimensionsBySubtype(
                    state,
                    collectionId,
                    AllocationDimensionKind.Generic
                );

                return object_type === ALLOCATION_DIMENSIONS_NAMESPACE && genericAllocationDimensions.length >= 1;
            }
        )
        .filter(({ payload: { operations } }) => {
            let isUpdatingRelevantDimension = false;
            for (const operation of operations || []) {
                // We only care about operation that touches dimension values
                if (
                    !operation ||
                    operation.operation !== OperationType.Set ||
                    operation.childKey !== AllocationDimensionFields.dimensionValues[0] ||
                    !operation.deltas
                ) {
                    continue;
                }

                isUpdatingRelevantDimension = operation.deltas.some((delta) =>
                    new RegExp(
                        `\/struct_value\/(?:fields\/${PURCHASE_ORDER_DOOR_GROUP_FIELD}|${AllocationDimensionValueFields.Included}|${AllocationDimensionValueFields.Active})\/?`,
                        "i"
                    ).test(delta.get("path"))
                );

                if (isUpdatingRelevantDimension) {
                    break;
                }
            }

            return isUpdatingRelevantDimension;
        })
        .map(
            ({
                payload: {
                    HEAD: { document_id },
                },
            }) => {
                // return Observable.empty();
                return startReconcilePOWithAllocationDimensions(document_id);
            }
        );

export const reconcilePOWithDeliveryEpic = (action$, store$) =>
    action$
        .ofType(COLLECTIONS.COLLECTION.DELIVERY_PURCHASE_ORDER.RECONCILE.START)
        .debounceTime(250)
        .concatMap(({ payload: { collectionId } }) => {
            const state = store$.getState();
            const genericAllocationDimensions = getAllocationDimensionsBySubtype(
                state,
                collectionId,
                AllocationDimensionKind.Generic
            );

            // Only proceed if there is more than one generic allocation dimensions
            if (genericAllocationDimensions.length <= 1) {
                return Observable.empty();
            }

            const deliveryPurchaseOrders = getCollectionDeliveryPurchaseOrders(state, collectionId);
            // We assume first generic dimension is delivery
            const deliveryAllocationDimension = genericAllocationDimensions[0];
            // We assume last generic dimension is door
            const doorAllocationDimension = genericAllocationDimensions[genericAllocationDimensions.length - 1];
            const reconciledDeliveryPOs = reconcilePOWithAllocationDimensionToMutate(
                deliveryPurchaseOrders,
                deliveryAllocationDimension,
                doorAllocationDimension
            );

            return reconciledDeliveryPOs.map((reconciledDeliveryPO) =>
                mutateDeliveryPurchaseOrder(
                    collectionId,
                    reconciledDeliveryPO.deliveryPurchaseOrder,
                    reconciledDeliveryPO.index,
                    { history: false }
                )
            );
        });
